/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import { ChartBarIcon, ClockIcon, UsersIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React from 'react'

import styles from '@/components/quality-explorer/ActivityCard.module.css'
import { ActivityCardInfoItem } from '@/components/quality-explorer/ActivityCardInfoItem'
import { Activity } from '@/components/quality-explorer/types'
import { cn } from '@/utils/css'

/** The colour configuration for the activity card. */
const COLOUR_CONFIG = {
  Red: {
    text: 'text-kits-team',
    border: 'border-kits-team',
    hex: '#d51818',
  },
  Green: {
    text: 'text-kits-insights',
    border: 'border-kits-insights',
    hex: '#1b9147',
  },
  Blue: {
    text: 'text-kits-mini',
    border: 'border-kits-mini',
    hex: '#2f93cd',
  },
} as const

/** A description of the available properties for this component. */
type ActivityCardProps = Activity

/**
 * A card that displays information about an activity in the qualities explorer. Displays core information around the
 * activity such as the name, time required, difficulty level etc. as well as a description of the activity. The
 * description is HTML, pulled from the database--there is some CSS to handle the formatting of this text.
 */
const ActivityCard: React.FC<ActivityCardProps> = ({
  name,
  descriptions,
  time,
  level,
  kolor,
  participants,
  kit_name,
}) => {
  const t = useTranslations('ActivityCard')
  const colourConfig = COLOUR_CONFIG[kolor]

  /** Process the descriptions to add the new CSS classes to any `ul` tag. */
  const processDescriptions = (text?: string): string => {
    return (
      text
        ?.replace(/<ul(.*?)class="(.*?)"(.*?)>/g, `<ul$1class="$2 ${styles.list}"$3>`)
        .replace(/<ul(?![^>]*\bclass=)/g, `<ul class="${styles.list}"`) ?? ''
    )
  }

  return (
    <div className={'bg-white rounded-2xl p-6 shadow-mta'}>
      <h3 className={cn('text-xl font-semibold mb-4', colourConfig.text)}>
        {name}: {kit_name}
      </h3>

      <ul className={cn('border-t-2 border-b-2 py-3 mb-4 space-y-2', colourConfig.border)}>
        <ActivityCardInfoItem icon={<ClockIcon className={'w-5 h-5 text-gray-500'} />} label={t('time')} value={time} />
        <ActivityCardInfoItem
          icon={<UsersIcon className={'w-5 h-5 text-gray-500'} />}
          label={t('participants')}
          value={participants}
        />
        <ActivityCardInfoItem
          icon={<ChartBarIcon className={'w-5 h-5 text-gray-500'} />}
          label={t('level')}
          value={level}
        />
      </ul>

      <div
        style={{ '--list-color': colourConfig.hex } as React.CSSProperties}
        className={'text-gray-800'}
        dangerouslySetInnerHTML={{
          __html: processDescriptions(descriptions),
        }}
      />
    </div>
  )
}

/**
 * A loading skeleton representation of the activity card.
 */
const ActivityCardSkeleton: React.FC = () => {
  const t = useTranslations('ActivityCard')

  return (
    <div className={'bg-white rounded-2xl p-6 shadow-mta'}>
      <div className={'h-6 bg-gray-100 rounded-lg animate-pulse w-full mb-2'} />

      <ul className={cn('border-t-2 border-b-2 py-3 mb-4 space-y-2 border-gray-200')}>
        <ActivityCardInfoItem
          icon={<ClockIcon className={'w-5 h-5 text-gray-500'} />}
          label={t('time')}
          value={<div className={'h-4 bg-gray-100 rounded-lg animate-pulse w-36'} />}
        />
        <ActivityCardInfoItem
          icon={<UsersIcon className={'w-5 h-5 text-gray-500'} />}
          label={t('participants')}
          value={<div className={'h-4 bg-gray-100 rounded-lg animate-pulse w-36'} />}
        />
        <ActivityCardInfoItem
          icon={<ChartBarIcon className={'w-5 h-5 text-gray-500'} />}
          label={t('level')}
          value={<div className={'h-4 bg-gray-100 rounded-lg animate-pulse w-36'} />}
        />
      </ul>
      <div className={'flex flex-col gap-2'}>
        <div className={'h-6 bg-gray-100 rounded-lg animate-pulse w-full'} />
        <div className={'h-6 bg-gray-100 rounded-lg animate-pulse w-5/6'} />
        <div className={'h-6 bg-gray-100 rounded-lg animate-pulse w-4/6'} />
        <div className={'h-6 bg-gray-100 rounded-lg animate-pulse w-3/6 '} />
      </div>
    </div>
  )
}

export { ActivityCard, ActivityCardSkeleton }
